/**
 * In order to determine the best connection strategy for WebRTC calls we run a
 * preflight check on the provided ICE servers by spinning up a short-lived
 * `RTCPeerConnection` for each URL and counting the candidate types it gathers
 * before timing out or reaching `iceGatheringState: complete`. This module
 * owns the selection logic. Console / Sentry formatting lives in the adjacent
 * `ice-preflight-logs.ts`.
 */

import {
  consoleLogIceSelection,
  consoleLogIceServers,
  consoleLogPreflightStart,
  consoleLogProbeResult,
  parseIceUrl,
} from './ice-preflight-logs';
import {
  type CandidateCounters,
  emptyCandidateCounters,
  tallyCandidate,
} from './webrtc-logger';

export interface IceUrlProbeResult {
  url: string;
  counters: CandidateCounters;
  elapsedMs: number;
  errorCount: number;
  timedOut: boolean;
}

type IceSelectionReason =
  | 'urls_filtered_to_working_set'
  | 'degraded_no_usable_candidates'
  | 'preflight_threw_fallback_to_google_stun';

type BrowserBlockedReason = 'browser_blocked_port';

interface BrowserBlockedUrl {
  url: string;
  reason: BrowserBlockedReason;
}

export interface IceSelection {
  iceServers: RTCIceServer[];
  iceTransportPolicy: RTCIceTransportPolicy;
  reason: IceSelectionReason;
  results: IceUrlProbeResult[];
  /**
   * URLs the signaling payload contained that were filtered out before
   * preflight because the browser refuses to use them (currently: any
   * port from `BROWSER_BLOCKED_PORTS`, e.g. 53). Surfaced so the Settings
   * panel can show "blocked by browser" rows alongside passed/failed
   * URLs without re-deriving the list from `wsIceServers`.
   */
  blockedByBrowser: BrowserBlockedUrl[];
  /**
   * Stable identifier surfaced into structured logs so existing Sentry
   * queries that filter by `ice_profile_id` keep working after the
   * profile concept was removed.
   */
  profileId: 'first-offer-mixed' | 'fallback-google-stun';
}

const ICE_PREFLIGHT_TIMEOUT_MS = 2500;

function urlsOf(server: RTCIceServer): string[] {
  if (Array.isArray(server.urls)) return server.urls;
  return [server.urls];
}

function isStunUrl(url: string): boolean {
  return /^stun:/i.test(url);
}

function isTurnUrl(url: string): boolean {
  return /^turns?:/i.test(url);
}

/**
 * Split each `RTCIceServer` into one entry per URL, preserving
 * `username` / `credential`. Multi-URL entries from Cloudflare commonly
 * pack several transport variants together, and lumping them into a
 * single probe target hides which transport actually works.
 */
export function splitIceServers(servers: RTCIceServer[]): RTCIceServer[] {
  const out: RTCIceServer[] = [];
  for (const server of servers) {
    for (const url of urlsOf(server)) {
      out.push({
        urls: url,
        ...(server.username !== undefined && { username: server.username }),
        ...(server.credential !== undefined && {
          credential: server.credential,
        }),
        // OAuth-flavoured TURN credentials must keep credentialType: 'oauth';
        // dropping it makes the browser fall back to password auth and the
        // server rejects the allocation.
        ...((server as RTCIceServer & { credentialType?: string })
          .credentialType !== undefined && {
          credentialType: (server as RTCIceServer & { credentialType?: string })
            .credentialType,
        }),
      } as RTCIceServer);
    }
  }
  return out;
}

function singleUrlOf(server: RTCIceServer): string {
  return urlsOf(server)[0];
}

function serverKey(server: RTCIceServer): string {
  return JSON.stringify({
    url: singleUrlOf(server),
    username: server.username ?? '',
    credential: server.credential ?? '',
    credentialType:
      (server as RTCIceServer & { credentialType?: string }).credentialType ??
      '',
  });
}

export function dedupeIceServers(servers: RTCIceServer[]): RTCIceServer[] {
  const seen = new Set<string>();
  return servers.filter((server) => {
    const key = serverKey(server);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Modern browsers refuse to send WebRTC traffic to certain ports — port
 * 53 in particular. Cloudflare's `/credentials` payload includes
 * `stun:...:53` and `turn:...:53` entries on its alternate-port list,
 * and per Cloudflare's own docs those URLs will time out in the
 * browser. Because ASP requires non-trickle ICE, a single timing-out
 * URL widens the whole gathering window — strip them up front.
 *
 * Run this on already-split servers (one URL per entry); URLs that
 * fail to parse are kept rather than guessed about.
 */
const BROWSER_BLOCKED_PORTS = new Set<number>([53]);

export function filterBrowserBlockedUrls(
  servers: RTCIceServer[],
): RTCIceServer[] {
  return servers.filter((server) => {
    const url = singleUrlOf(server);
    const info = parseIceUrl(url);
    if (!info) return true;
    return !BROWSER_BLOCKED_PORTS.has(info.port);
  });
}

/**
 * Probe a single ICE URL by spinning up a short-lived `RTCPeerConnection`
 * with only that server configured, opening a data channel, and counting
 * the candidate types the browser gathers before `iceGatheringState`
 * reaches `complete` or the timeout fires.
 */
function probeIceUrl(
  server: RTCIceServer,
  timeoutMs = ICE_PREFLIGHT_TIMEOUT_MS,
): Promise<IceUrlProbeResult> {
  const url = singleUrlOf(server);
  const startedAt = performance.now();
  const counters = emptyCandidateCounters();
  let errorCount = 0;
  let timedOut = false;
  let settled = false;
  let timeout: ReturnType<typeof setTimeout> | null = null;
  let pc: RTCPeerConnection | null = null;

  return new Promise((resolve) => {
    const finish = () => {
      if (settled) return;
      settled = true;
      if (timeout) clearTimeout(timeout);
      pc?.close();
      resolve({
        url,
        counters,
        elapsedMs: Math.round(performance.now() - startedAt),
        errorCount,
        timedOut,
      });
    };

    try {
      pc = new RTCPeerConnection({ iceServers: [server] });

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          tallyCandidate(counters, event.candidate);
        }
      };
      pc.onicecandidateerror = () => {
        errorCount += 1;
      };
      pc.onicegatheringstatechange = () => {
        if (pc?.iceGatheringState === 'complete') {
          finish();
        }
      };

      pc.createDataChannel('ice-preflight');
      timeout = setTimeout(() => {
        timedOut = true;
        finish();
      }, timeoutMs);

      void pc
        .createOffer()
        .then((offer) => pc?.setLocalDescription(offer))
        .catch(() => {
          errorCount += 1;
          finish();
        });
    } catch {
      errorCount += 1;
      finish();
    }
  });
}

function isStunServer(server: RTCIceServer): boolean {
  return isStunUrl(singleUrlOf(server));
}

function isTurnServer(server: RTCIceServer): boolean {
  return isTurnUrl(singleUrlOf(server));
}

/**
 * A STUN URL is considered usable when the browser successfully gathered
 * a server-reflexive candidate through it; a TURN URL when it produced
 * a relay candidate. Host candidates do not validate either route.
 */
function isUsableProbe(
  server: RTCIceServer,
  result: IceUrlProbeResult,
): boolean {
  if (isStunServer(server)) {
    return result.counters.srflx > 0;
  }
  if (isTurnServer(server)) {
    return result.counters.relay > 0;
  }
  return false;
}

type ProbeFn = (
  server: RTCIceServer,
  timeoutMs?: number,
) => Promise<IceUrlProbeResult>;

/**
 * Run preflight on every URL in `cloudflareServers` plus
 * `googleStunServers`, then assemble a single ICE config from only the
 * URLs that produced a usable candidate. If only TURN URLs survive, set
 * `iceTransportPolicy: 'relay'` so the real offer is not padded with
 * dead host candidates that the browser already knows go nowhere.
 *
 * `probe` is injectable for tests; production code uses the default
 * `probeIceUrl` which spins up real `RTCPeerConnection`s.
 */
export async function chooseIceServers(
  cloudflareServers: RTCIceServer[],
  googleStunServers: RTCIceServer[],
  probe: ProbeFn = probeIceUrl,
): Promise<IceSelection> {
  consoleLogIceServers('received Cloudflare ICE servers', cloudflareServers);
  consoleLogIceServers('using Google STUN fallback servers', googleStunServers);

  const splitCloudflare = dedupeIceServers(splitIceServers(cloudflareServers));
  const splitGoogle = dedupeIceServers(splitIceServers(googleStunServers));
  const beforeBlockFilter = dedupeIceServers([
    ...splitCloudflare,
    ...splitGoogle,
  ]);
  const allTargets = filterBrowserBlockedUrls(beforeBlockFilter);
  const blockedByBrowser: BrowserBlockedUrl[] = beforeBlockFilter
    .filter((server) => !allTargets.includes(server))
    .map((server) => ({
      url: singleUrlOf(server),
      reason: 'browser_blocked_port',
    }));
  if (blockedByBrowser.length > 0) {
    console.info(
      `[WebRTC ICE] dropped ${blockedByBrowser.length} URL(s) on browser-blocked ports`,
      blockedByBrowser.map(({ url }) => url),
    );
  }

  consoleLogPreflightStart(allTargets);

  const probed: { server: RTCIceServer; result: IceUrlProbeResult }[] =
    await Promise.all(
      allTargets.map(async (server) => ({
        server,
        result: await probe(server),
      })),
    );

  probed.forEach(({ result }) => consoleLogProbeResult(result));

  const working = probed.filter(({ server, result }) =>
    isUsableProbe(server, result),
  );
  const results = probed.map(({ result }) => result);

  if (working.length === 0) {
    const selection: IceSelection = {
      iceServers: dedupeIceServers(splitGoogle),
      iceTransportPolicy: 'all',
      reason: 'degraded_no_usable_candidates',
      results,
      blockedByBrowser,
      profileId: 'fallback-google-stun',
    };
    consoleLogIceSelection(selection);
    return selection;
  }

  const workingServers = dedupeIceServers(working.map(({ server }) => server));
  const allTurn = workingServers.every(isTurnServer);
  const selection: IceSelection = {
    iceServers: workingServers,
    iceTransportPolicy: allTurn ? 'relay' : 'all',
    reason: 'urls_filtered_to_working_set',
    results,
    blockedByBrowser,
    profileId: 'first-offer-mixed',
  };
  consoleLogIceSelection(selection);
  return selection;
}
